/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  Button,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@repo/ui";
import { Filter, CircleX } from "lucide-react";
import KendoMultiselect from "ui/multiselect";

interface DatePreset {
  id: string;
  name: string;
}

interface FieldEngineer {
  employeeId: string;
  name: string;
}

interface OverviewFiltersProps {
  DATE_PRESETS: DatePreset[];

  overviewFilters: any;
  selectedGroups: any[];
  selectedFieldEngineers: FieldEngineer[];
  selectedAccounts: any[];
  selectedRegions: any[];

  assignmentGroups?: any[];
  fieldEngineer?: any[];
  customers?: any[];
  regions?: any[];

  setSelectedGroups: (v: any[]) => void;
  setSelectedFieldEngineers: (v: FieldEngineer[]) => void;
  setSelectedAccounts: (v: any[]) => void;
  setSelectedRegions: (v: any[]) => void;

  updateOverviewFilter: (key: string, value?: any) => void;
  syncOverviewFiltersToUrl: (
    date: string | undefined,
    assignmentGroups: string[],
    fieldEngineer: string[],
  ) => void;

  handleApplyOverviewFilters: () => void;
  handleClearOverviewFilters: () => void;
}

export default function OverviewFilters({
  DATE_PRESETS,
  overviewFilters,
  selectedGroups,
  selectedFieldEngineers,
  selectedAccounts,
  selectedRegions,
  assignmentGroups,
  fieldEngineer,
  customers,
  regions,
  setSelectedGroups,
  setSelectedFieldEngineers,
  setSelectedAccounts,
  setSelectedRegions,
  updateOverviewFilter,
  syncOverviewFiltersToUrl,
  handleApplyOverviewFilters,
  handleClearOverviewFilters,
}: OverviewFiltersProps) {
  return (
    <div className="flex gap-3 items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="secondary">
            Overview Filters <Filter size={16} className="ml-2" />
          </Button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-[80vw] p-4">
          <div className="grid grid-cols-5 gap-3">
            {/* Date Preset */}
            <Select
              value={overviewFilters.date?.id}
              onValueChange={(v) => {
                const selectedDate = DATE_PRESETS.find((d) => d.id === v);
                updateOverviewFilter("date", selectedDate);

                syncOverviewFiltersToUrl(
                  selectedDate?.id,
                  selectedGroups.map((g) => g.name),
                  selectedFieldEngineers.map((f) => f.employeeId),
                );
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {DATE_PRESETS.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Field Engineers */}
            <KendoMultiselect
              data={fieldEngineer || []}
              textField="name"
              dataItemKey="employeeId"
              value={selectedFieldEngineers}
              onChange={(e: any) => {
                const value = e.value;

                const finalValue = value.some(
                  (v: any) => v.employeeId === "all",
                )
                  ? [{ employeeId: "all", name: "All" }]
                  : value;

                setSelectedFieldEngineers(finalValue);

                syncOverviewFiltersToUrl(
                  overviewFilters.date?.id,
                  selectedGroups.map((g) => g.name),
                  finalValue.map((f: any) => f.employeeId),
                );
              }}
              placeholder="Field Engineers"
            />

            {/* Assignment Groups */}
            <KendoMultiselect
              data={assignmentGroups || []}
              textField="name"
              dataItemKey="name"
              value={selectedGroups}
              onChange={(e: any) => {
                const value = e.value;
                setSelectedGroups(value);

                syncOverviewFiltersToUrl(
                  overviewFilters.date?.id,
                  value.map((g: any) => g.name),
                  selectedFieldEngineers.map((f) => f.employeeId),
                );
              }}
              placeholder="Assignment Groups"
            />

            {/* Accounts */}
            <KendoMultiselect
              data={customers || []}
              textField="name"
              dataItemKey="customerId"
              value={selectedAccounts}
              onChange={(e: any) => {
                setSelectedAccounts(e.value);
              }}
              placeholder="Accounts"
            />

            {/* Regions */}
            {/* <KendoMultiselect
              data={regions || []}
              textField="name"
              dataItemKey="name"
              value={selectedRegions}
              onChange={(e: any) => {
                setSelectedRegions(e.value);
              }}
              placeholder="Regions"
            /> */}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={handleClearOverviewFilters}>
              Clear
            </Button>

            <Button variant="secondary" onClick={handleApplyOverviewFilters}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Active Filter Badges */}
      <div className="flex gap-2 flex-wrap">
        {overviewFilters.date ? (
          <Badge variant="secondary" className="gap-2 pr-1">
            {overviewFilters.date.name}
            <CircleX
              size={14}
              onClick={() => {
                updateOverviewFilter("date");
              }}
            />
          </Badge>
        ) : null}

        {selectedFieldEngineers.map((fe) => (
          <Badge key={fe.employeeId} variant="secondary">
            {fe.name}
            <CircleX
              size={14}
              onClick={() => {
                setSelectedFieldEngineers(
                  selectedFieldEngineers.filter(
                    (f) => f.employeeId !== fe.employeeId,
                  ),
                );
              }}
            />
          </Badge>
        ))}

        {selectedGroups.map((g: any) => (
          <Badge key={g.name} variant="secondary">
            {g.name}
            <CircleX
              size={14}
              onClick={() => {
                setSelectedGroups(
                  selectedGroups.filter((x) => x.name !== g.name),
                );
              }}
            />
          </Badge>
        ))}
        {selectedAccounts.map((a: any) => (
          <Badge key={a.name} variant="secondary">
            {a.name}
            <CircleX
              size={14}
              onClick={() => {
                setSelectedAccounts(
                  selectedAccounts.filter((x) => x.name !== a.name),
                );
              }}
            />
          </Badge>
        ))}
        {selectedRegions.map((r: any) => (
          <Badge key={r.name} variant="secondary">
            {r.name}
            <CircleX
              size={14}
              onClick={() => {
                setSelectedRegions(
                  selectedRegions.filter((x) => x.name !== r.name),
                );
              }}
            />
          </Badge>
        ))}
      </div>
    </div>
  );
}
